'use client';
import { forwardRef, useMemo } from 'react';
import { template1PlaceholderData, TemplateProps } from '@zeus/shared';
import useFont from '../../../hooks/useFont';
import { getScaleSize } from '../../../utils/utils';
import Menu from './components/menu';
import { useNormalizedPixels } from '../../../hooks/useResize';
import Media from '../Media';
import BrandLogo from './components/BrandLogo';
import useSecondColumnFirstItem from '../../../hooks/useSecondColumnFirstItem';
import { BoxComponent } from '../../../base/box/Box';
import { TypographyComponent } from '../../../base/typography/Typography';

export const Template1 = forwardRef<HTMLDivElement, TemplateProps>((props, ref) => {
  const { template, scale = 1, style, development, timezone, tenantId, active, shouldLoad = true } = props;

  useFont(template?.fontFamily);
  const { normalizePixels } = useNormalizedPixels();

  const isBrandLogo = template?.brandLogo && template?.brandLogo?.isEnabled && template?.brandLogo?.image;
  const content = Array.isArray(template?.content) ? template?.content : [];
  const menus = content.length > 0 ? content : template1PlaceholderData;
  const { content: templateContent, ...rest } = template;
  const isDescription = template?.itemDescription?.isEnabled;
  const media = template?.media?.mediaContent;
  const isMedia = template.media?.isEnabled && media?.url;
  const isLayoutOne = isMedia && template.layoutType === 1;
  const isLayoutTwo = isMedia && template.layoutType === 2;
  const result = Array.isArray(menus) && menus.length > 0 ? menus : [];

  const contentKey = useMemo(() => {
    if (!Array.isArray(result)) return '';
    return result
      .map((m) => {
        const cats = (m.categories || [])
          .map((c) => {
            const items = (c.items || []).map((i) => i.id).join(',');
            return `${c.id}[${items}]`;
          })
          .join('|');
        return `${m.id}:{${cats}}`;
      })
      .join('||');
  }, [result]);

  const { containerRef, secondColumnFirstChild } = useSecondColumnFirstItem(contentKey, isBrandLogo as boolean);

  const bgStyles = () => {
    if (template?.background?.isEnabled && template?.background?.image) {
      return { backgroundImage: `url(${template?.background?.image})` };
    }

    if (template?.background?.color) {
      return { backgroundColor: template?.background?.color };
    }

    return {
      backgroundImage: `url('https://dashboard-demo-assests.s3.us-west-2.amazonaws.com/dashboard/menuboard/Template1.webp')`,
    };
  };

  const rowGap = useMemo(() => {
    if (isBrandLogo) {
      return isDescription ? '1.6%' : '1%';
    }
    return isDescription ? '1.4%' : '1.2%';
  }, [isDescription, isBrandLogo]);

  return (
    <div
      style={{
        backgroundSize: 'cover',
        padding: isLayoutOne ? '0px' : '2% 5%',
        overflow: 'hidden',
        position: 'relative',
        display: 'flex',
        ...bgStyles(),
        ...(scale ? { transform: `scale(1)`, transformOrigin: 'top left' } : {}),
        ...(!style ? { height: '100vh', width: '100vw' } : style),
        ...(isLayoutOne ? { justifyContent: 'space-between' } : {}),
      }}
    >
      <div
        ref={containerRef}
        style={{
          width: isLayoutOne ? '67%' : '100%',
          padding: isLayoutOne ? '2% 3%' : '0px',
        }}
      >
        <div
          ref={ref}
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            flexDirection: 'column',
            height: '100%',
            alignContent: 'flex-start',
            overflow: 'hidden',
            rowGap: rowGap,
            columnGap: '2%',
            position: 'relative',
            paddingBottom: '24px',
          }}
        >
          {isBrandLogo && (
            <BrandLogo scale={scale} src={template?.brandLogo?.image as string} isLayoutOne={isLayoutOne as boolean} />
          )}
          {result.map((menu, menuIndex) => (
            <Menu
              key={menu.id}
              menu={menu}
              template={rest}
              development={development}
              menuIndex={menuIndex}
              timezone={timezone}
              scale={scale}
              tenantId={tenantId}
              column2FirstChild={secondColumnFirstChild?.id as string}
            />
          ))}

          {isLayoutTwo && (
            <div id="template-1-layout-2-media" style={{ width: '32%', position: 'absolute', top: '0', left: '34%' }}>
              <Media
                media={media}
                height={getScaleSize(normalizePixels(500, 'height'), scale)}
                playing={active}
                shouldLoad={shouldLoad}
                isDevelopment={development}
              />
            </div>
          )}
          {template.taxLabel.isEnabled && (
            <BoxComponent sx={{ position: 'absolute', left: '0%', bottom: '0%' }}>
              <TypographyComponent
                sx={{ color: '#fff', fontWeight: '500', fontSize: normalizePixels(12, 'height') }}
                variant="subtitle1"
              >
                {template.taxLabel.taxText}
              </TypographyComponent>
            </BoxComponent>
          )}
        </div>
      </div>

      {isLayoutOne && (
        <div style={{ width: '33%', position: 'absolute', right: '0', height: '100%' }}>
          <Media
            isDevelopment={development}
            media={media}
            borderRadius={'0px'}
            playing={active}
            shouldLoad={shouldLoad}
          />
        </div>
      )}
    </div>
  );
});
